import { faFolder, faLock, faUnlock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	TableContainer,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { CerebroLink, CerebroText } from '../../shared/components';
import { ActiveDataRoom, CurrentUser, DataRoomDocument, DataRoomFolder, DataRoomRepository } from '../../shared/models';
import { DateUtils, FileUtils } from '../../shared/utils';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { GetDocumentTypeDescription } from '../../shared/utils/enum';
import { DocumentActionButton } from './DocumentActionButton';
import { DocumentFolderActionButton } from './DocumentFolderActionButton';

interface FolderDisplayProps {
	dataRoomGuid: string | undefined;
	repository: DataRoomRepository | null | undefined;
	currentUser: CurrentUser;
	activeDeals: ActiveDataRoom[] | undefined;
	changeFolder: (folder: DataRoomFolder) => void;
	editFolder: (folder: DataRoomFolder) => void;
	deleteFolder: (folder: DataRoomFolder) => void;
	sendFolder: (folder: DataRoomFolder) => void;
	editDocument: (document: DataRoomDocument) => void;
	moveDocument: (document: DataRoomDocument) => void;
	copyTo: (document: DataRoomDocument) => void;
	downloadHistory: (document: DataRoomDocument) => void;
	deleteDocument: (document: DataRoomDocument) => void;
	sendDocument: (document: DataRoomDocument) => void;
	linkDocument: (document: DataRoomDocument) => void;
	copyDocuments: (folder: DataRoomFolder) => void;
}

export const FolderDisplay: React.FC<FolderDisplayProps> = ({
	dataRoomGuid,
	repository,
	currentUser,
	activeDeals,
	changeFolder,
	editFolder,
	deleteFolder,
	sendFolder,
	editDocument,
	moveDocument,
	copyTo,
	downloadHistory,
	deleteDocument,
	sendDocument,
	linkDocument,
	copyDocuments,
}) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	if (!repository) return <></>;

	return (
		<TableContainer>
			<Table>
				<TableHead>
					<TableRow>
						<TableCell>Name</TableCell>
						{!isMobile && (
							<>
								{!currentUser.isBankUser && !currentUser.isTeamMemberUser && (
									<TableCell>Date Modified</TableCell>
								)}
								<TableCell>Type</TableCell>
								{!currentUser.isBankUser && <TableCell>Access</TableCell>}
							</>
						)}
						{!currentUser.isBankUser && <TableCell></TableCell>}
					</TableRow>
				</TableHead>
				<TableBody>
					{repository.folder.folders.map(folder => {
						return (
							<TableRow key={folder.guid}>
								<TableCell>
									<CerebroLink variant="link" onClick={() => changeFolder(folder)}>
										<FontAwesomeIcon icon={faFolder} color={theme.palette.primary.main} />{' '}
										{folder.name}
									</CerebroLink>
								</TableCell>
								{!isMobile && (
									<>
										{!currentUser.isBankUser && !currentUser.isTeamMemberUser && (
											<TableCell>
												{DateUtils.FormatStringAsShortDate(folder.modifiedOn?.toString())}
											</TableCell>
										)}
										<TableCell></TableCell>
										{!currentUser.isBankUser && (
											<TableCell>
												<CerebroText>
													{folder.isVisibleToAll ? (
														<FontAwesomeIcon
															title="Folder is accessible to all lenders"
															icon={faUnlock}
														/>
													) : (
														<FontAwesomeIcon
															title="Folder is restricted to specific lenders"
															icon={faLock}
														/>
													)}
												</CerebroText>
											</TableCell>
										)}
									</>
								)}
								{!currentUser.isBankUser && (
									<TableCell>
										<DocumentFolderActionButton
											isEmployee={currentUser.isEmployeeUser}
											folder={folder}
											activeDeals={activeDeals}
											onEdit={editFolder}
											onDelete={deleteFolder}
											onSend={sendFolder}
											onCopyDocuments={copyDocuments}
										/>
									</TableCell>
								)}
							</TableRow>
						);
					})}
					{repository.folder.files.map(file => {
						return (
							<TableRow key={file.guid}>
								<TableCell>
									<CerebroLink
										href={CerebroApiEndpoints.Documents.AbsoluteDownload(dataRoomGuid, file.guid)}
										target="_blank"
										rel="noreferrer"
										variant="link"
									>
										{FileUtils.getFileTypeIcon(file.fileName)} {file.name}
									</CerebroLink>
								</TableCell>
								{!isMobile && (
									<>
										{!currentUser.isBankUser && !currentUser.isTeamMemberUser && (
											<TableCell>
												{DateUtils.FormatStringAsShortDate(file.documentDate?.toString())}
											</TableCell>
										)}
										<TableCell>{GetDocumentTypeDescription(file.documentType)}</TableCell>
										{!currentUser.isBankUser && (
											<TableCell>
												<CerebroText>
													{repository.folder.isVisibleToAll && file.isVisibleToAll ? (
														<FontAwesomeIcon
															title="Visible to all lenders"
															icon={faUnlock}
														/>
													) : (
														<FontAwesomeIcon
															title={
																file.isVisibleToAll
																	? 'Restricted to specific lenders by document access permissions'
																	: 'Restricted to specific lenders by folder access permissions'
															}
															icon={faLock}
														/>
													)}
												</CerebroText>
											</TableCell>
										)}
									</>
								)}
								{!currentUser.isBankUser && (
									<TableCell>
										<DocumentActionButton
											isEmployee={currentUser.isEmployeeUser}
											isBanker={currentUser.isBankUser}
											document={file}
											editDocument={editDocument}
											moveDocument={moveDocument}
											copyTo={copyTo}
											downloadHistory={downloadHistory}
											deleteDocument={deleteDocument}
											sendDocument={sendDocument}
											linkDocument={linkDocument}
										/>
									</TableCell>
								)}
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
		</TableContainer>
	);
};
